import { useEffect, useState, useCallback } from 'react';
import analyticsService, { AnalyticsEvent } from '../services/analyticsService';

export interface WidgetEvent {
  data: {
    type:
      | 'widget:showInfo'
      | 'widget:close'
      | 'external:event'
      | 'widget:requestFeedback'
      | 'widget:open'
      | 'widget:analytics';
    eventName?: string;
    data?: object;
  };
}

interface UseWidgetCommunicationProps {
  onInfoModalClose?: (time: number) => void;
  onFeedbackRequest?: () => void;
  infoModalCloseTime?: number | null;
  isInfoModalEnabled?: boolean;
}

export const useWidgetCommunication = ({
  onInfoModalClose,
  onFeedbackRequest,
  infoModalCloseTime,
  isInfoModalEnabled = true,
}: UseWidgetCommunicationProps = {}) => {
  const [showInformationModal, setShowInformationModal] = useState<boolean>(isInfoModalEnabled);

  const handleWidgetEvent = useCallback(
    (event: WidgetEvent) => {
      const { type, eventName, data } = event.data;

      switch (type) {
        case 'external:event': // Host page events captured by widget.js
          analyticsService.trackEvent({
            eventName: `external:${eventName}`,
            properties: data as Record<string, unknown>,
          });
          break;

        case 'widget:open': // Widget opened event captured by widget.js
          analyticsService.trackEvent({
            eventName: 'clickWidgetOpenButton',
            properties: data as Record<string, unknown>,
          });
          break;

        case 'widget:showInfo': // Widget header button for showing info modal has been clicked
          if (isInfoModalEnabled) {
            setShowInformationModal(true);
          }
          if (onInfoModalClose) {
            onInfoModalClose(0); // Reset close time
          }
          break;

        case 'widget:analytics': // Non-external analytics events from the widget, e.g. close or minimize button clicks
          if (eventName) {
            analyticsService.trackEvent({
              eventName: eventName as AnalyticsEvent,
              properties: data as Record<string, unknown>,
            });
          }
          break;

        case 'widget:close': {
          // Widget close button clicked
          if (!infoModalCloseTime) {
            window.parent.postMessage('widget:close', '*');
            return;
          }

          const timeSinceInfoModalClose = Date.now() - infoModalCloseTime;
          const hasBeenInAppLongEnough = timeSinceInfoModalClose >= 10000;

          if (hasBeenInAppLongEnough && onFeedbackRequest) {
            // Show feedback modal and notify widget (but don't close)
            onFeedbackRequest();
            window.parent.postMessage({ type: 'widget:feedbackModalOpen' }, '*');
          } else {
            // Close immediately if not enough time has passed
            window.parent.postMessage('widget:close', '*');
          }
          break;
        }
      }
    },
    [infoModalCloseTime, isInfoModalEnabled, onInfoModalClose, onFeedbackRequest]
  );

  const handleCookieEvent = useCallback((event: WidgetEvent) => {
    if (event.data.eventName === 'cookieAccepted') {
      setShowInformationModal(false);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('message', handleWidgetEvent);
    window.addEventListener('message', handleCookieEvent);

    return () => {
      window.removeEventListener('message', handleWidgetEvent);
      window.removeEventListener('message', handleCookieEvent);
    };
  }, [handleWidgetEvent, handleCookieEvent]);

  const handleInformationModalClose = useCallback(() => {
    setShowInformationModal(false);
    if (onInfoModalClose) {
      onInfoModalClose(Date.now());
    }
  }, [onInfoModalClose]);

  useEffect(() => {
    setShowInformationModal(isInfoModalEnabled);
  }, [isInfoModalEnabled]);

  return {
    showInformationModal,
    handleInformationModalClose,
  };
};
